import type { SidebarItem } from "@/types/navigation";
import { matchPath } from "react-router-dom";

export function normalizePath(pathname: string) {
  if (pathname === "/") return pathname;

  return pathname.replace(/\/+$/, "");
}

export function pathMatches(pathname: string, basePath: string) {
  const normalizedPathname = normalizePath(pathname);
  const normalizedBasePath = normalizePath(basePath);

  return (
    normalizedPathname === normalizedBasePath ||
    normalizedPathname.startsWith(`${normalizedBasePath}/`)
  );
}

function pathMatchesItem(pathname: string, itemPath: string) {
  const normalizedPathname = normalizePath(pathname);
  const normalizedItemPath = normalizePath(itemPath);

  return (
    normalizedPathname === normalizedItemPath ||
    normalizedPathname.startsWith(`${normalizedItemPath}/`)
  );
}

/**
 * Recursively finds the best-matching active item id in a (potentially nested)
 * list of sidebar items.  The item whose `href` or `basePath` is the longest
 * prefix-match of `pathname` wins.
 *
 * Empty strings are ignored so that group containers without a real route
 * never steal the active state from their children.
 */
export function getActiveSidebarItemId(
  pathname: string,
  items: SidebarItem[],
  fallbackItemId: string | null = null,
) {
  let activeItemId: string | null = null;
  let activeItemScore = -1;
  let activeItemDepth = -1;

  function isBetterMatch(score: number, depth: number) {
    return score > activeItemScore || (score === activeItemScore && depth > activeItemDepth);
  }

  function walk(list: SidebarItem[], depth = 0) {
    for (const item of list) {
      if (item.shortcut) continue;

      for (const pattern of item.activePaths ?? []) {
        if (!matchPath({ path: pattern, end: true }, normalizePath(pathname))) continue;

        const score = normalizePath(pattern).length;
        if (isBetterMatch(score, depth)) {
          activeItemId = item.id;
          activeItemScore = score;
          activeItemDepth = depth;
        }
      }

      const candidates = [item.href, item.basePath].filter(
        (candidate): candidate is string => candidate != null && candidate !== "",
      );

      for (const candidate of candidates) {
        if (!pathMatchesItem(pathname, candidate)) continue;

        const score = normalizePath(candidate).length;

        if (isBetterMatch(score, depth)) {
          activeItemId = item.id;
          activeItemScore = score;
          activeItemDepth = depth;
        }
      }

      if (item.children?.length) {
        walk(item.children, depth + 1);
      }
    }
  }

  walk(items);

  return activeItemId ?? fallbackItemId;
}

// ── Helpers for collapsible-group state ──────────────────────────────────────

/**
 * Returns `true` when `item` or any of its descendants matches `activeItemId`.
 */
export function hasActiveDescendant(item: SidebarItem, activeItemId: string | null): boolean {
  if (!activeItemId) return false;
  if (item.id === activeItemId) return true;
  return item.children?.some((child) => hasActiveDescendant(child, activeItemId)) ?? false;
}

/**
 * Walk the full tree and collect ids of every group (item with children) that
 * contains the active item, so those groups can be auto-opened.
 */
export function collectOpenGroupIds(
  items: SidebarItem[],
  activeItemId: string | null,
): Set<string> {
  const ids = new Set<string>();

  function walk(list: SidebarItem[]) {
    for (const item of list) {
      if (item.children?.length) {
        if (hasActiveDescendant(item, activeItemId)) {
          ids.add(item.id);
        }
        walk(item.children);
      }
    }
  }

  walk(items);

  return ids;
}
